import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { speakersAPI, sessionsAPI, eventsAPI } from '../../lib/api';
import { ArrowLeft, Linkedin, Rocket, Mic2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useSEO } from '../../hooks/useSEO';
import ShareButton from '../../components/common/ShareButton';

export const SpeakerDetailPage = () => {
  const { slug } = useParams();
  const [speaker, setSpeaker] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  // SEO with speaker name, image and bio
  useSEO(
    speaker ? `${speaker.first_name} ${speaker.last_name}` : '',
    [],
    speaker?.image || '',
    speaker?.bio?.substring(0, 160) || (speaker ? `${speaker.first_name} ${speaker.last_name} - ${speaker.title} @ ${speaker.company}` : '')
  );

  useEffect(() => {
    loadData();
  }, [slug]);

  const loadData = async () => {
    try {
      const speakerRes = await speakersAPI.getOne(slug);
      setSpeaker(speakerRes.data);

      // Get sessions where this speaker is involved
      const sessionsRes = await sessionsAPI.getAll();
      const speakerSessions = sessionsRes.data.filter(s => 
        s.speaker_ids?.includes(speakerRes.data.id)
      );

      // Enrich sessions with event data
      const enrichedSessions = await Promise.all(
        speakerSessions.map(async (session) => {
          try {
            const eventRes = await eventsAPI.getOne(session.event_id);
            return { ...session, event: eventRes.data };
          } catch {
            return session;
          }
        })
      );

      setSessions(enrichedSessions);
    } catch (error) {
      console.error('Error loading speaker:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse-glow w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
          <Rocket className="text-primary animate-bounce" size={32} />
        </div>
      </div>
    );
  }

  if (!speaker) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-2xl font-bold mb-4">Speaker non trovato</h1>
          <Link to="/speakers">
            <Button variant="outline" className="rounded-full">
              <ArrowLeft className="mr-2" size={16} />
              Torna agli Speakers
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="py-12 px-6" data-testid="speaker-detail-page">
      <div className="max-w-5xl mx-auto">
        {/* Back Link */}
        <Link 
          to="/speakers" 
          className="inline-flex items-center text-sm text-muted-foreground hover:text-primary mb-8 transition-colors"
        >
          <ArrowLeft size={16} className="mr-2" />
          Torna agli Speakers
        </Link>

        {/* Speaker Profile */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-16">
          <div className="md:col-span-1">
            <div className="aspect-[3/4] rounded-2xl overflow-hidden sticky top-24">
              <img 
                src={speaker.image || `https://ui-avatars.com/api/?name=${speaker.first_name}+${speaker.last_name}&background=7B2CBF&color=fff&size=400`}
                alt={`${speaker.first_name} ${speaker.last_name}`}
                className="w-full h-full object-cover"
                data-testid="speaker-image"
              />
            </div>
          </div>

          <div className="md:col-span-2 space-y-8">
            <div>
              <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight mb-4" data-testid="speaker-name">
                {speaker.first_name} {speaker.last_name}
              </h1>

              {(speaker.title || speaker.company) && (
                <p className="text-xl text-primary mb-4">
                  {speaker.title && speaker.title}
                  {speaker.title && speaker.company && ' @ '}
                  {speaker.company && speaker.company}
                </p>
              )}

              {speaker.linkedin && (
                <a 
                  href={speaker.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass hover:border-primary/50 transition-all text-sm"
                  data-testid="speaker-linkedin"
                >
                  <Linkedin size={16} className="text-primary" />
                  LinkedIn
                </a>
              )}
              
              {/* Share Button */}
              <ShareButton 
                type="speaker" 
                slug={speaker.slug || slug} 
                title={`${speaker.first_name} ${speaker.last_name}`} 
              />
            </div>

            {speaker.bio && (
              <div>
                <h2 className="font-heading text-lg font-semibold mb-4">Bio</h2>
                <p className="text-muted-foreground leading-relaxed whitespace-pre-line" data-testid="speaker-bio">
                  {speaker.bio}
                </p>
              </div>
            )}

            {/* Sessions */}
            {sessions.length > 0 && (
              <div>
                <h2 className="font-heading text-lg font-semibold mb-4">
                  Interventi ({sessions.length})
                </h2>
                <div className="space-y-4">
                  {sessions.map((session) => (
                    <article 
                      key={session.id}
                      className="glass rounded-xl p-6 hover:border-primary/30 transition-all"
                      data-testid={`session-${session.id}`}
                    >
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                          <Mic2 className="text-primary" size={18} />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-heading font-semibold mb-1">
                            {session.title}
                          </h3>
                          {session.event && (
                            <Link 
                              to={`/events/${session.event.slug}`}
                              className="text-sm text-primary hover:underline"
                            >
                              {session.event.title}
                            </Link>
                          )}
                          {session.description && (
                            <p className="text-sm text-muted-foreground mt-2">
                              {session.description}
                            </p>
                          )}
                          <p className="text-xs font-mono text-muted-foreground mt-2">
                            {session.start_time} - {session.end_time}
                          </p>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
